import { useCallback, useState, useRef } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { TooltipProvider } from "@/components/ui/tooltip";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn, removeProperty } from "@/lib/utils";

export type SelectOption<T = unknown> = {
  value: string | number;
  label: string;
  raw?: T;
};

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  description?: string;
  autoComplete?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  onValueChange?: (value: string | number) => void;
  preserveMessageSpace?: boolean;
  required?: boolean;
};

export const FormSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  control,
  label,
  name,
  description,
  wrapperClassName,
  labelClassName,
  triggerClassName,
  contentClassName,
  descriptionClassName,
  messageClassName,
  placeholder = "Select option...",
  disabled,
  onValueChange,
  preserveMessageSpace = false,
  required,
  ...props
}: FormSelectProps<TFieldValues, TName>) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [contentAlign, setContentAlign] = useState<"start" | "end">("start");

  const optionLabel = useCallback(
    (value: number | string) => {
      return (
        options.find((option) => option.value.toString() === value.toString())
          ?.label ?? placeholder
      );
    },
    [options, placeholder]
  );

  const handleOpenChange = (open: boolean) => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      setContentAlign(rect.left > window.innerWidth / 2 ? "end" : "start");
    }
  };

  return (
    <TooltipProvider>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <FormItem
            className={cn("aero:w-full", wrapperClassName)}
            aria-invalid={!!fieldState?.error}
          >
            {label && (
              <FormLabel
                className={cn(
                  required &&
                    "aero:flex aero:items-center aero:justify-between",
                  labelClassName
                )}
              >
                {label}
                {required && (
                  <span className="aero:text-destructive" aria-hidden="true">
                    *
                  </span>
                )}
              </FormLabel>
            )}
            <FormControl>
              <Select
                disabled={disabled}
                {...removeProperty(field, "ref")}
                value={field.value?.toString() ?? ""}
                onOpenChange={handleOpenChange}
                onValueChange={(value) => {
                  // Find original option to get the correct type (string or number)
                  const originalOption = options.find(
                    (opt) => opt.value.toString() === value
                  );
                  const typedValue = originalOption?.value ?? value;

                  if (onValueChange) {
                    onValueChange(typedValue);
                  }
                  field.onChange(typedValue);
                }}
              >
                <Tooltip>
                  <TooltipTrigger className="aero:truncate">
                    <SelectTrigger
                      ref={triggerRef}
                      className={cn(
                        "aero:w-full ",
                        fieldState?.error &&
                          "aero:border-destructive aero:ring-destructive",
                        triggerClassName
                      )}
                    >
                      <SelectValue
                        className="aero:truncate aero:font-inter aero:font-normal aero:text-sm aero:leading-5"
                        placeholder={
                          <span className="aero:text-sm aero:text-neutral-500">
                            {placeholder}
                          </span>
                        }
                      />
                    </SelectTrigger>
                  </TooltipTrigger>
                  {field.value && (
                    <TooltipContent
                      side="top"
                      align="start"
                      className="aero:max-w-prose aero:break-words"
                    >
                      {optionLabel(field.value)}
                    </TooltipContent>
                  )}
                </Tooltip>
                <SelectContent
                  className={cn(
                    "aero:max-w-[var(--radix-select-trigger-width)]",
                    contentClassName
                  )}
                  side="bottom"
                  align={contentAlign}
                  sideOffset={4}
                  avoidCollisions={true}
                >
                  {options.map((option) => (
                    <SelectItem
                      value={option.value.toString()}
                      key={`${name}-${option.value}`}
                    >
                      <span className="aero:text-sm aero:truncate">
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            {description && (
              <FormDescription className={descriptionClassName}>
                {description}
              </FormDescription>
            )}
            <div
              className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}
            >
              <FormMessage
                className={cn(
                  fieldState?.error
                    ? "aero:text-destructive aero:text-xs"
                    : preserveMessageSpace
                      ? "aero:invisible aero:text-xs"
                      : "aero:hidden",
                  messageClassName
                )}
              />
            </div>
          </FormItem>
        )}
        {...props}
      />
    </TooltipProvider>
  );
};
